import { buffers, channel, eventChannel } from 'redux-saga';
import { call, cancelled, put, race, select, spawn, take } from 'redux-saga/effects';
import { throwIfAborted } from '../../../utils/abortSignal';
import { RequestError } from '../../../utils/request';
import { resetAltchaState } from '../actions';
import { LOAD_FLOW_SUCCESS, LOAD_NODE_SUCCESS, RESET_FLOW } from '../constants';
import { isAltchaProvider, isRecaptchaProvider } from '../utils/captcha';
import { getRecaptchaToken, isRecaptchaRuntimeFailure } from '../utils/recaptcha';
import { activateAltchaProvider, waitForAltchaToken, waitForCaptchaContextReady } from './captchaSaga';

const interrupted = () => new RequestError('abort', 'Submission captcha preparation was interrupted');
const isCurrentFlow = (state, captured) =>
  state.node.id === captured.node.id &&
  state.flow.id === captured.flow.id &&
  state.flow.submissionContext === captured.flow.submissionContext;

function* prepareCaptcha(requirements, captured) {
  let usedAltcha = false;

  try {
    yield* waitForCaptchaContextReady();

    const state = yield select();
    let provider = requirements.provider || state.node.captchaProvider;

    if (isRecaptchaProvider(provider)) {
      try {
        return { provider, token: yield call(getRecaptchaToken) };
      } catch (error) {
        if (!isRecaptchaRuntimeFailure(error) || globalThis.navigator?.onLine === false) throw error;

        yield* activateAltchaProvider();
        provider = (yield select()).node.captchaProvider;
      }
    }

    if (!isAltchaProvider(provider)) throw new RequestError('captcha', 'Captcha provider is not configured');

    usedAltcha = true;

    if (!(yield* waitForAltchaToken())) throw new RequestError('captcha', 'Captcha verification failed');

    return { provider, token: (yield select()).flow.altchaToken };
  } finally {
    if (usedAltcha && isCurrentFlow(yield select(), captured)) yield put(resetAltchaState());
  }
}

function* getSubmissionCaptcha(requirements, { signal }, captured) {
  throwIfAborted(signal);

  if (!isCurrentFlow(yield select(), captured)) throw interrupted();

  const aborts = eventChannel((emit) => {
    const onAbort = () => emit(true);
    signal?.addEventListener('abort', onAbort, { once: true });

    return () => signal?.removeEventListener('abort', onAbort);
  });

  try {
    const { captcha } = yield race({
      captcha: call(prepareCaptcha, requirements, captured),
      replaced: take([LOAD_FLOW_SUCCESS, LOAD_NODE_SUCCESS, RESET_FLOW]),
      aborted: take(aborts),
    });

    if (!captcha) throw signal?.reason || interrupted();

    return captcha;
  } finally {
    aborts.close();
  }
}

/**
 * The session requests foreground captcha only when its retained online job runs.
 * The provider lives as long as that job, so a cancelled or deduplicated caller
 * cannot interrupt preparation another caller is waiting on.
 */
export function* submitWithForegroundCaptcha(session, input, options, captured) {
  if (!options.isOnline || !input.captcha.required || options.getCaptchaToken) {
    return yield call([session, session.submit], input, options);
  }

  const requests = channel(buffers.expanding());
  let closed = false;
  const getCaptchaToken = (requirements, context) =>
    new Promise((resolve, reject) => {
      if (closed) reject(interrupted());
      else requests.put({ requirements, context, resolve, reject });
    });

  const worker = yield spawn(function* () {
    while (true) {
      const request = yield take(requests);

      try {
        request.resolve(yield call(getSubmissionCaptcha, request.requirements, request.context, captured));
      } catch (error) {
        request.reject(error);
      } finally {
        if (yield cancelled()) request.reject(interrupted());
      }
    }
  });

  const submission = session.submit(input, { ...options, getCaptchaToken });
  const close = () => {
    closed = true;
    worker.cancel();
    requests.close();
  };

  submission.then(close, close);

  return yield call(() => submission);
}
